import React from 'react';
import { Box, Heading, Icon, Table, Tooltip } from '@zillow/constellation-10';
import { useIsScreenDesktop } from '@zg-rentals/rental-manager-env';
import { StyledContainer } from './StyledComponents';
import { EVENT_IDS } from '../../model/ClickstreamEvents';
import type { AdproductsZuoraBillingInvoiceDto } from '@zg-rentals/ts-adproducts-client';
import { IconCalendarOutline } from '@zillow/constellation-icons';

const mapDaysSpotlighted = (daysSpotlighted: Array<{ year: number; month: number; day: number }>) => {
  if (!daysSpotlighted || daysSpotlighted.length === 0) {
    return '-';
  }
  return daysSpotlighted.map((date) => {
    const dateStr = `${date.month}/${date.day}/${date.year}`;
    return (
      <div key={dateStr} className="InvoicePage-active-range">
        {dateStr}
      </div>
    );
  });
};

export interface ListingSpotlightTableProps {
  currentInvoice: AdproductsZuoraBillingInvoiceDto;
}

export const ListingSpotlightTable = ({ currentInvoice }: ListingSpotlightTableProps) => {
  const isScreenDesktop = useIsScreenDesktop();

  const spotlightBreakdowns = (currentInvoice?.sfhLineItems ?? []).filter((item) => item.totalSpotlightedDays > 0);

  if (spotlightBreakdowns.length === 0) {
    return null;
  }

  return (
    <StyledContainer data-eventid={EVENT_IDS.SET_FILTER} data-eventdescription="Set filter event">
      <Heading level={4}>Listing Spotlight</Heading>
      <Box css={{ margin: 'xs' }} />
      {isScreenDesktop ? (
        <Table.Root
          appearance="horizontal"
          aria-label="Listing Spotlight table"
          alignments={['start', 'start', 'start', 'end']}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Address</Table.HeaderCell>
              <Table.HeaderCell>Unit</Table.HeaderCell>
              <Table.HeaderCell>Product</Table.HeaderCell>
              <Table.HeaderCell>Total Days Spotlighted</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {spotlightBreakdowns.map((item) => (
              <Table.Row key={item.chargeId}>
                <Table.Cell>{item.address}</Table.Cell>
                <Table.Cell>{item.unit || '-'}</Table.Cell>
                <Table.Cell>Listing Spotlight</Table.Cell>
                <Table.Cell>
                  {!item.totalSpotlightedDays ? (
                    '-'
                  ) : (
                    <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Tooltip
                        trigger={
                          <Box>
                            {item.totalSpotlightedDays}
                            <Icon size="md">
                              <IconCalendarOutline />
                            </Icon>
                          </Box>
                        }
                      >
                        {mapDaysSpotlighted(item.daysSpotlighted)}
                      </Tooltip>
                    </Box>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      ) : (
        <Table.Root appearance="horizontal" aria-label="Listing Spotlight table" alignments={['start', 'start', 'end']}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Address</Table.HeaderCell>
              <Table.HeaderCell>Unit</Table.HeaderCell>
              <Table.HeaderCell>Total Days</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {spotlightBreakdowns.map((item) => (
              <Table.Row key={item.chargeId}>
                <Table.Cell>{item.address}</Table.Cell>
                <Table.Cell>{item.unit || '-'}</Table.Cell>
                <Table.Cell>{item.totalSpotlightedDays}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      )}
      <Box css={{ margin: 'xs' }} />
    </StyledContainer>
  );
};
